<template>
  <v-app>
    <CrmSpaStaleUpdateRoot />
    <CrmPullToRefreshHost
      :active="showMenu"
      :mobile="isMobile || forcedMobileMode"
      :drawer-open="drawer && (isMobile || isMobileMode || forcedMobileMode)"
    />
    <v-navigation-drawer 
      app 
      v-if="showMenu && (!isMobile || drawer)"
      v-model="drawer"
      :temporary="isMobile || isMobileMode || forcedMobileMode"
      :permanent="!isMobile && !isMobileMode && !forcedMobileMode"
      @input="onDrawerChange"
      class="sidebar-nav"
    >
      <v-list-item>
        <v-list-item-content>
          <v-list-item-title class="text-h6">
            <v-img contain position="left" max-height="50" alt="logo"
              :src="company?.settings?.logo || require('@/assets/logo.png')" />
          </v-list-item-title>
          <v-list-item-subtitle>
            CRM "МояФабрика" v.{{ appVersion }}
          </v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>
      <v-list-item link to="/companies" v-if="!onCustomHost">
        <v-list-item-content>
          <v-list-item-title>
            {{ company?.settings?.brand_name || "Выберите организацию" }}
          </v-list-item-title>
        </v-list-item-content>
        <v-list-item-icon>
          <v-icon>mdi-menu-right-outline</v-icon>
        </v-list-item-icon>
      </v-list-item>
      <v-list-item link to="/auth/branches" v-if="availableBranchSelect">
        <v-list-item-content>
          <v-list-item-title>
            {{ branch?.name || "Выберите филиал" }}
          </v-list-item-title>
        </v-list-item-content>
        <v-list-item-icon>
          <v-icon>mdi-menu-right-outline</v-icon>
        </v-list-item-icon>
      </v-list-item>
      <v-divider></v-divider>

      <v-list dense nav class="sidebar-menu">
        <div v-for="item in visibleRoutes" :key="item.title">
          <v-list-item 
            v-if="!item.children" 
            link 
            :to="item.to" 
            :class="['menu-item', { 'new-year-menu-item': item.isNewYear }]"
          >
            <v-list-item-icon>
              <v-icon :class="{ 'new-year-icon': item.isNewYear }">{{ item.icon }}</v-icon>
            </v-list-item-icon>

            <v-list-item-content>
              <v-list-item-title>
                <v-badge :content="item.badge" v-if="item.badge">
                  {{ item.title }}
                </v-badge>
                <div v-else :class="{ 'new-year-title': item.isNewYear }">{{ item.title }}</div>
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-list-group v-else class="menu-group">
            <template v-slot:activator>
              <v-list-item link>
                <v-list-item-icon>
                  <v-icon>{{ item.icon }}</v-icon>
                </v-list-item-icon>

                <v-list-item-content>
                  <v-list-item-title>
                    {{ item.title }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </template>
            <v-list-item class="menu-sub-item" :to="child.to" :key="child.title" v-for="(child, j) in item.children" link>
              <v-list-item-icon>
                <v-icon>{{ child.icon }}</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>
                  {{ child.title }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list-group>
        </div>
        <v-list-item @click="logout()">
          <v-list-item-icon>
            <v-icon>mdi-logout</v-icon>
          </v-list-item-icon>

          <v-list-item-content>
            <v-list-item-title>Выйти</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </v-navigation-drawer>
    <v-main :class="{ 'mobile-main': showMenu && (isMobile || forcedMobileMode) }">
      <AppTopBar :show="showMenu && !isCustomer" />
      <v-system-bar dark color="red lighten-2" v-if="showMenu && !company.subscription">
        <v-icon>mdi-alert</v-icon>
        <span>Подписка истекла</span>
      </v-system-bar>
      <v-alert v-if="showMenu && isImpersonating" dense text color="warning" class="banner-alert">
        <v-row align="center">
          <v-col class="grow">
            <v-icon left>mdi-account-switch</v-icon>
            Вы находитесь в режиме клиента. Действия выполняются от лица клиента.
            <span v-if="impersonation?.impersonator?.name"> Исходный пользователь: {{ impersonation.impersonator.name }}.</span>
          </v-col>
          <v-col class="shrink d-flex gap-2">
            <v-btn color="warning" small @click="exitCustomerMode">
              <v-icon left small>mdi-undo-variant</v-icon>
              Вернуться в CRM
            </v-btn>
          </v-col>
        </v-row>
      </v-alert>
      <TaxSettingsNotification v-if="showMenu && !isImpersonating" />
      <v-alert v-if="showMenu && !isImpersonating && subscriptionExpiresSoon" dense text color="warning">
        <v-row align="center">
          <v-col class="grow">
            <v-icon left>mdi-clock-alert</v-icon>
            Подписка истекает через {{ daysUntilExpiration }} {{ daysUntilExpiration === 1 ? 'день' : daysUntilExpiration < 5 ? 'дня' : 'дней' }}. Необходимо продлить подписку для продолжения работы.
          </v-col>
          <v-col class="shrink">
            <v-btn color="warning" small @click="$router.push('/subscription')">Продлить</v-btn>
          </v-col>
        </v-row>
      </v-alert>
      <v-alert v-if="showMenu && !isImpersonating && shouldShowTelegramBanner" dense text color="primary" class="banner-alert">
        <v-row align="center">
          <v-col class="grow">
            <v-icon left>mdi-send</v-icon>
            Подпишитесь на Telegram бота, чтобы оперативно получать все уведомления
          </v-col>
          <v-col class="shrink d-flex gap-2">
            <v-btn color="secondary" small @click="tgSubscribe()">
              <v-icon left small>mdi-send</v-icon>
              Подписаться
            </v-btn>
            <v-menu offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon small v-bind="attrs" v-on="on">
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item @click="hideBannerForDays('telegram', 3)">
                  <v-list-item-icon>
                    <v-icon small>mdi-clock-outline</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Скрыть на 3 дня</v-list-item-title>
                </v-list-item>
                <v-list-item @click="hideBannerForever('telegram')">
                  <v-list-item-icon>
                    <v-icon small>mdi-close-circle</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Скрыть навсегда</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-col>
        </v-row>
      </v-alert>

      <v-alert v-if="showMenu && !isImpersonating && shouldShowMaxBanner" dense text color="primary" class="banner-alert">
        <v-row align="center">
          <v-col class="grow">
            <v-icon left>mdi-message-text</v-icon>
            Подключите MAX-бота CRM — уведомления и команды без открытия браузера
          </v-col>
          <v-col class="shrink d-flex gap-2">
            <v-btn color="secondary" small @click="maxSubscribe()">
              <v-icon left small>mdi-message-text</v-icon>
              Подключить MAX
            </v-btn>
            <v-menu offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon small v-bind="attrs" v-on="on">
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item @click="hideBannerForDays('max', 3)">
                  <v-list-item-icon>
                    <v-icon small>mdi-clock-outline</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Скрыть на 3 дня</v-list-item-title>
                </v-list-item>
                <v-list-item @click="hideBannerForever('max')">
                  <v-list-item-icon>
                    <v-icon small>mdi-close-circle</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Скрыть навсегда</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-col>
        </v-row>
      </v-alert>
      
      <v-alert v-if="showMenu && !isImpersonating && canInstallPWA" dense text color="success" class="banner-alert">
        <v-row align="center">
          <v-col class="grow">
            <v-icon left>mdi-download</v-icon>
            Установите CRM на главный экран для быстрого доступа
          </v-col>
          <v-col class="shrink d-flex gap-2">
            <v-btn color="success" small @click="installApp()">
              <v-icon left small>mdi-cellphone-arrow-down</v-icon>
              Установить
            </v-btn>
            <v-menu offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon small v-bind="attrs" v-on="on">
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item @click="hideBannerForDays('pwaInstall', 3)">
                  <v-list-item-icon>
                    <v-icon small>mdi-clock-outline</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Скрыть на 3 дня</v-list-item-title>
                </v-list-item>
                <v-list-item @click="hideBannerForever('pwaInstall')">
                  <v-list-item-icon>
                    <v-icon small>mdi-close-circle</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Скрыть навсегда</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-col>
        </v-row>
      </v-alert>
      
      <v-alert v-if="showMenu && !isImpersonating && shouldShowSmtpDisabledBanner" dense text color="warning" class="banner-alert">
        <v-row align="center">
          <v-col class="grow">
            <v-icon left>mdi-email-off</v-icon>
            Отправка уведомлений через SMTP CRM отключена администратором. Для использования уведомлений
            <router-link to="/settings?tab=smtp" class="font-weight-bold">настройте свой SMTP</router-link>.
          </v-col>
          <v-col class="shrink d-flex gap-2">
            <v-menu offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon small v-bind="attrs" v-on="on">
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item @click="hideSmtpBannerForDays(3)">
                  <v-list-item-icon>
                    <v-icon small>mdi-clock-outline</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Скрыть на 3 дня</v-list-item-title>
                </v-list-item>
                <v-list-item @click="hideSmtpBannerForDays(7)">
                  <v-list-item-icon>
                    <v-icon small>mdi-calendar</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Скрыть на 1 неделю</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-col>
        </v-row>
      </v-alert>

      <v-alert v-if="showMenu && !isImpersonating && canEnablePushOnThisDevice" dense text color="info" class="banner-alert">
        <v-row align="center">
          <v-col class="grow">
            <v-icon left>mdi-bell-ring</v-icon>
            Включите push-уведомления на этом устройстве для получения важных событий
          </v-col>
          <v-col class="shrink d-flex gap-2">
            <v-btn color="primary" small @click="requestPushPermission()">
              <v-icon left small>mdi-bell-check</v-icon>
              Включить
            </v-btn>
            <v-menu offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon small v-bind="attrs" v-on="on">
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item @click="hideBannerForDays('push', 3)">
                  <v-list-item-icon>
                    <v-icon small>mdi-clock-outline</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Скрыть на 3 дня</v-list-item-title>
                </v-list-item>
                <v-list-item @click="hideBannerForever('push')">
                  <v-list-item-icon>
                    <v-icon small>mdi-close-circle</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Скрыть навсегда</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-col>
        </v-row>
      </v-alert>

      <v-container 
        v-if="$route.path !== '/year-summary'"
        fluid 
        :class="{ 'mobile-container': showMenu && (isMobile || forcedMobileMode) }"
      >
        <router-view />
        <YearSummaryBanner />
      </v-container>
      <router-view v-else />
    </v-main>
    <v-bottom-navigation app shift v-if="showMenu && (isMobile || forcedMobileMode)" class="bottom-nav">
      <template v-if="isCustomer">
        <v-btn to="/customer-home" icon>
          <span>Главная</span>
          <v-icon>mdi-view-dashboard</v-icon>
        </v-btn>
        <v-btn to="/customer-orders" icon>
          <span>Заказы</span>
          <v-icon>mdi-cart-outline</v-icon>
        </v-btn>
        <v-btn to="/customer-documents" icon>
          <span>Документы</span>
          <v-icon>mdi-file-document-outline</v-icon>
        </v-btn>
        <v-btn to="/customer-payments" icon>
          <span>Платежи</span>
          <v-icon>mdi-cash</v-icon>
        </v-btn>
        <v-btn @click="drawer = !drawer" icon>
          <span>Меню</span>
          <v-icon>mdi-menu</v-icon>
        </v-btn>
      </template>
      <template v-else>
        <v-btn to="/money" icon v-if="company && company.access_level === 2">
          <span>Деньги</span>
          <v-icon>mdi-cash</v-icon>
        </v-btn>
        <v-btn to="/orders" icon>
          <span>Заказы</span>
          <v-icon>mdi-cart-outline</v-icon>
        </v-btn>
        <v-btn to="/" icon>
          <span>Главная</span>
          <v-icon>mdi-view-dashboard</v-icon>
        </v-btn>
        <v-btn to="/customers" icon v-if="company && company.access_level === 1">
          <span>Клиенты</span>
          <v-icon>mdi-toolbox</v-icon>
        </v-btn>
        <v-btn @click="drawer = !drawer" icon>
          <span>Меню</span>
          <v-icon>mdi-menu</v-icon>
        </v-btn>
      </template>
    </v-bottom-navigation>
    
    <!-- Кнопка "Закрыть все" для уведомлений -->
    <ClearAllNotificationsButton v-if="showMenu && !isImpersonating" />
  </v-app>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from "vuex";
import axios from "@/plugins/axios";
// Swal теперь доступен глобально через this.$swal
import Echo from "@/plugins/echo";
import { APP_VERSION } from '@/config/version';
// Firebase Push Notifications
import { initializeFirebase } from '@/firebase/config';
import { 
  getFCMToken, 
  subscribeToMessages, 
  isPushNotificationSupported,
  getNotificationPermission,
  showLocalNotification
} from '@/firebase/pushNotifications';
// Clear All Notifications Button
import ClearAllNotificationsButton from '@/components/ClearAllNotificationsButton.vue';
import AppTopBar from '@/components/AppTopBar.vue';
import YearSummaryBanner from '@/components/YearSummary/YearSummaryBanner.vue';
import TaxSettingsNotification from '@/components/TaxSettingsNotification.vue';
import CrmSpaStaleUpdateRoot from '@/components/Spa/CrmSpaStaleUpdateRoot.vue';
import CrmPullToRefreshHost from '@/components/Spa/CrmPullToRefreshHost.vue';
import errorHandler from '@/services/errorHandler'
import { resolvePushNotificationTag, resolvePushNotificationUrl } from '@/utils/pushNotificationNavigation'

export default Vue.extend({
  name: 'App',
  
  components: {
    AppTopBar,
    TaxSettingsNotification,
    ClearAllNotificationsButton,
    YearSummaryBanner,
    CrmSpaStaleUpdateRoot,
    CrmPullToRefreshHost,
  },

  data() {
    return {
      bottom: null,
      drawer: !this.$vuetify.breakpoint.mobile,
      isMobileMode: false,
      channel: null,
      userChannel: null,
      telegramChannel: null,
      // PWA установка
      deferredPrompt: null,
      showInstallButton: false,
      // Push-уведомления
      deviceId: null, // Уникальный ID устройства (UUID)
      fcmToken: null,
      pushSupported: isPushNotificationSupported(), // Инициализируем СРАЗУ синхронно!
      pushPermission: 'default',
      pushConnectedOnDevice: false, // Флаг из backend - подключено ли push на текущем устройстве
      // Управление баннерами
      hidePwaInstallBanner: false,
      hidePushBanner: false,
      hideTelegramBanner: false,
      hideMaxBanner: false,
      hideSmtpDisabledBanner: false,
    }
  },
  methods: {
    /**
     * Прижимаем нижнее меню к низу viewport.
     * bottom всегда 0; safe-area — только через padding/height в CSS
     * (не через bottom: inset — иначе меню «висит» над низом экрана).
     */
    pinBottomNavigation() {
      const el = document.querySelector('.v-bottom-navigation.bottom-nav');
      if (!el) return false;

      // Сбрасываем containing-block на корне/wrap (legacy PTR, залипшие inline)
      const app = document.querySelector('.v-application');
      if (app && app.style.transform) {
        app.style.transform = '';
        app.style.transition = '';
      }
      const wrap = document.querySelector('.v-application--wrap');
      if (wrap && wrap.style.transform) {
        wrap.style.transform = '';
        wrap.style.transition = '';
      }
      // Legacy AppDialog scroll-lock мог оставить body fixed + top:-Y
      if (document.body.style.position === 'fixed') {
        const top = document.body.style.top;
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        if (top) {
          const y = Math.abs(parseInt(top, 10) || 0);
          window.scrollTo(0, y);
        }
      }

      el.style.setProperty('position', 'fixed', 'important');
      el.style.setProperty('bottom', '0', 'important');
      el.style.setProperty('left', '0', 'important');
      el.style.setProperty('right', '0', 'important');
      el.style.setProperty('top', 'auto', 'important');
      el.style.setProperty('width', '100%', 'important');
      el.style.setProperty('max-width', '100%', 'important');
      el.style.setProperty('margin', '0', 'important');
      el.style.setProperty('margin-bottom', '0', 'important');
      el.style.setProperty('transform', 'none', 'important');
      el.style.setProperty('-webkit-transform', 'none', 'important');
      return true;
    },
    // Совместимость со старыми вызовами
    fixBottomNavigationOnIOS() {
      this.pinBottomNavigation();
    },
    // Перепривязка при скролле/resize (меню появляется через v-if после логина)
    fixBottomNavigationOnScroll() {
      if (this._bottomNavScrollHandlers) {
        this._bottomNavScrollHandlers.forEach((cleanup) => cleanup());
        this._bottomNavScrollHandlers = null;
      }

      const fixPosition = () => {
        this.pinBottomNavigation();
      };

      fixPosition();

      let scrollRaf = 0;
      const handleScroll = () => {
        if (scrollRaf) cancelAnimationFrame(scrollRaf);
        scrollRaf = requestAnimationFrame(fixPosition);
      };

      const handleVisibility = () => {
        if (document.visibilityState === 'visible') {
          fixPosition();
        }
      };

      window.addEventListener('scroll', handleScroll, { passive: true, capture: true });
      document.addEventListener('scroll', handleScroll, { passive: true, capture: true });
      window.addEventListener('resize', fixPosition, { passive: true });
      window.addEventListener('orientationchange', fixPosition, { passive: true });
      document.addEventListener('visibilitychange', handleVisibility);

      this._bottomNavScrollHandlers = [
        () => window.removeEventListener('scroll', handleScroll, { capture: true }),
        () => document.removeEventListener('scroll', handleScroll, { capture: true }),
        () => window.removeEventListener('resize', fixPosition),
        () => window.removeEventListener('orientationchange', fixPosition),
        () => document.removeEventListener('visibilitychange', handleVisibility),
      ];
    },
    ensureBottomNavigationPinned() {
      this.$nextTick(() => {
        if (!this.pinBottomNavigation()) {
          // Меню ещё не в DOM (v-if) — повторим кратко
          setTimeout(() => this.pinBottomNavigation(), 50);
          setTimeout(() => this.pinBottomNavigation(), 300);
        }
        this.fixBottomNavigationOnScroll();
      });
    },
    // ФИКС: Устанавливаем РЕАЛЬНУЮ высоту viewport (без адресной строки браузера)
    setRealViewportHeight() {
      // Используем visualViewport API если доступен (более точный для мобильных)
      let height;
      if (window.visualViewport) {
        height = window.visualViewport.height;
      } else {
        height = window.innerHeight;
      }
      
      // Вычисляем 1% от высоты
      const vh = height * 0.01;
      
      // Устанавливаем CSS переменную --vh
      document.documentElement.style.setProperty('--vh', `${vh}px`);
      
      // Для iOS - также устанавливаем точную высоту в пикселях
      document.documentElement.style.setProperty('--real-height', `${height}px`);
    },
    
    // КРИТИЧНЫЙ ФИКС: убираем двойной скролл на мобильных
    // Проблема: Vuetify и браузерные CSS перебивают обычные CSS правила
    // Решение: Устанавливаем inline styles с !important через JavaScript
    fixMobileOverflow() {
      // Список элементов и их overflow значений
      const overflowConfig = [
        { selector: 'html', x: 'hidden', y: 'auto' },
        { selector: 'body', x: 'hidden', y: 'visible' },
        { selector: '.v-application', x: 'hidden', y: 'visible' },
        { selector: '.v-main', x: 'hidden', y: 'visible' },
        { selector: '.dashboard-page-new', overflow: 'visible' },
        { selector: '.directory-page', overflow: 'visible' },
        { selector: '.dashboard-page', overflow: 'visible' }
      ];
      
      overflowConfig.forEach(({ selector, x, y, overflow }) => {
        const element = selector === 'html' ? document.documentElement 
                      : selector === 'body' ? document.body 
                      : document.querySelector(selector);
        
        if (element) {
          if (overflow) {
            element.style.setProperty('overflow', overflow, 'important');
          } else {
            element.style.setProperty('overflow-x', x, 'important');
            element.style.setProperty('overflow-y', y, 'important');
          }
        }
      });
      
      // Убрали назойливый console.log - всё работает корректно
    },
    
    async logout() {
      await this.$store.dispatch("LOGOUT");
      this.$router.push('/login').catch(() => {})
    },
    async tgSubscribe() {
      if (!this.user || !this.user.id) {
        return;
      }
      try {
        const data = await axios.post('auth/profile/telegram/link');
        const url = data?.telegram_link?.url;
        if (url) {
          window.open(url, '_blank');
        }
      } catch (error) {
        console.error('Failed to get Telegram link:', error);
      }
    },
    async maxSubscribe() {
      if (!this.user || !this.user.id) {
        return;
      }
      try {
        const data = await axios.post('auth/profile/max/link');
        const url = data?.max_link?.url || data?.max_link?.max_deep_link || data?.url;
        if (url) {
          window.open(url, '_blank');
        }
      } catch (error) {
        console.error('Failed to get MAX link:', error);
      }
    },
    // PWA установка
    async installApp() {
      if (!this.deferredPrompt) {
        console.log('❌ Промпт установки недоступен');
        return;
      }
      
      try {
        // Показываем промпт установки
        this.deferredPrompt.prompt();
        
        // Ждем выбора пользователя
        const { outcome } = await this.deferredPrompt.userChoice;
        
        if (outcome === 'accepted') {
          console.log('✅ Пользователь установил приложение');
          this.$swal.fire({
            icon: 'success',
            title: 'Приложение установлено!',
            text: 'Теперь вы можете запускать CRM прямо с главного экрана',
            timer: 3000,
            showConfirmButton: false
          });
        } else {
          console.log('❌ Пользователь отклонил установку');
        }
        
        // Очищаем промпт
        this.deferredPrompt = null;
        this.showInstallButton = false;
      } catch (error) {
        errorHandler.handle(error, { showDialog: false, logToConsole: false });
      }
    },
    checkPWAInstalled() {
      // Проверяем, запущено ли приложение как PWA
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches 
        || window.navigator.standalone 
        || document.referrer.includes('android-app://');
      
      if (isStandalone) {
        console.log('✅ Приложение запущено в режиме PWA');
      }
      
      return isStandalone;
    },
    // === Device ID ===
    /**
     * Генерирует стабильный device_id на основе браузера и типа устройства
     * Один браузер + один тип устройства = один device_id
     * Это предотвращает множественные токены для одного устройства
     */
    getOrCreateDeviceId() {
      const deviceInfo = this.getDeviceInfo();
      
      // Создаем ключ для localStorage на основе браузера и типа устройства
      const storageKey = `device_id_${deviceInfo.browser}_${deviceInfo.deviceType}`;
      
      // Проверяем, есть ли уже device_id для этой комбинации браузер+тип устройства
      let deviceId = localStorage.getItem(storageKey);
      
      if (!deviceId) {
        // Генерируем стабильный UUID v4 на основе браузера и типа устройства
        // Используем простой хеш для создания детерминированного UUID
        const seed = `${deviceInfo.browser}_${deviceInfo.deviceType}_${navigator.userAgent}`;
        deviceId = this.generateStableUUID(seed);
        
        // Сохраняем в localStorage навсегда для этой комбинации
        localStorage.setItem(storageKey, deviceId);
        
        // Также сохраняем общий device_id для обратной совместимости
        localStorage.setItem('device_id', deviceId);
      } else {
        // Обновляем общий device_id для обратной совместимости
        localStorage.setItem('device_id', deviceId);
      }
      
      return deviceId;
    },
    
    /**
     * Генерирует стабильный UUID v4 на основе seed (детерминированный)
     * Использует простой хеш для создания UUID из строки seed
     */
    generateStableUUID(seed) {
      // Простой хеш для создания детерминированного UUID
      let hash = 0;
      for (let i = 0; i < seed.length; i++) {
        const char = seed.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32bit integer
      }
      
      // Создаем массив байтов из хеша (используем несколько итераций для большего разнообразия)
      const bytes = [];
      let tempHash = Math.abs(hash);
      for (let i = 0; i < 16; i++) {
        bytes.push((tempHash + i * 17) % 256);
        tempHash = (tempHash * 31 + i) & 0xFFFFFFFF;
      }
      
      // Генерируем UUID v4 на основе байтов
      bytes[6] = (bytes[6] & 0x0f) | 0x40; // Version 4
      bytes[8] = (bytes[8] & 0x3f) | 0x80; // Variant 10
      
      const hex = bytes.map(b => b.toString(16).padStart(2, '0')).join('');
      const uuid = [
        hex.substring(0, 8),
        hex.substring(8, 12),
        hex.substring(12, 16),
        hex.substring(16, 20),
        hex.substring(20, 32)
      ].join('-');
      
      return uuid;
    },
    // === Push-уведомления ===
    async initializePushNotifications() {
      // Получаем или создаем device_id
      this.deviceId = this.getOrCreateDeviceId();
      
      // pushSupported уже установлен в data()
      // Получаем только permission
      this.pushPermission = await getNotificationPermission();
      
      if (!this.pushSupported) {
        return;
      }
      
      // Проверяем, есть ли уже сохранённый токен
      const savedToken = localStorage.getItem('fcm_token');
      if (savedToken && this.pushPermission === 'granted') {
        this.fcmToken = savedToken;
      }
      
      // Инициализируем Firebase
      try {
        initializeFirebase();
        
        // Если разрешение уже есть, получаем токен (или обновляем существующий)
        if (this.pushPermission === 'granted') {
          await this.registerPushToken();
        }
        
        // Подписываемся на сообщения
        this.subscribeToPushMessages();
      } catch (error) {
        errorHandler.handle(error, { showDialog: false, logToConsole: false });
      }
    },
    async registerPushToken() {
      try {
        // ВАЖНО: Убеждаемся, что device_id существует
        if (!this.deviceId) {
          this.deviceId = this.getOrCreateDeviceId();
        }
        
        const token = await getFCMToken();
        
        if (token) {
          this.fcmToken = token;
          this.pushPermission = 'granted';
          
          // Сохраняем токен локально для проверки на текущем устройстве
          localStorage.setItem('fcm_token', token);
          
          // Сохраняем токен на backend
          await this.saveFCMTokenToBackend(token);
          
          // Принудительно обновляем Vue, чтобы computed свойства пересчитались
          this.$nextTick(() => {
            this.$forceUpdate();
          });
        }
      } catch (error) {
        errorHandler.handle(error, { showDialog: false, logToConsole: false });
      }
    },
    async saveFCMTokenToBackend(token) {
      try {
        // КРИТИЧНО: device_id должен быть обязательно!
        if (!this.deviceId) {
          this.deviceId = this.getOrCreateDeviceId();
        }
        
        // Определяем тип устройства и браузер
        const deviceInfo = this.getDeviceInfo();
        
        const payload = {
          device_id: this.deviceId,
          token: token,
          device_type: deviceInfo.deviceType,
          browser: deviceInfo.browser,
          device_name: deviceInfo.deviceName
        };
        
        // Отправляем на backend (с device_id!)
        await axios.post('/fcm-tokens', payload);
        
        // После успешного сохранения запрашиваем актуальный статус с сервера
        const response = await this.$store.dispatch('getAuthUser');
        
        if (response && typeof response.push_connected_on_device !== 'undefined') {
          this.pushConnectedOnDevice = response.push_connected_on_device;
          
          // Принудительно обновляем Vue для пересчета computed properties
          this.$nextTick(() => {
            this.$forceUpdate();
          });
        }
      } catch (error) {
        errorHandler.handle(error, { showDialog: false, logToConsole: false });
      throw error; // Пробрасываем ошибку дальше для обработки
      }
    },
    getDeviceInfo() {
      const ua = navigator.userAgent;
      let browser = 'Unknown';
      let deviceType = 'desktop';
      
      // Определяем браузер
      if (ua.includes('Chrome') && !ua.includes('Edg')) {
        browser = 'Chrome';
      } else if (ua.includes('Firefox')) {
        browser = 'Firefox';
      } else if (ua.includes('Safari') && !ua.includes('Chrome')) {
        browser = 'Safari';
      } else if (ua.includes('Edg')) {
        browser = 'Edge';
      } else if (ua.includes('Opera') || ua.includes('OPR')) {
        browser = 'Opera';
      }
      
      // Определяем тип устройства
      if (/Android/i.test(ua)) {
        deviceType = 'mobile';
      } else if (/iPhone|iPad|iPod/i.test(ua)) {
        deviceType = 'mobile';
      } else if (/Tablet|iPad/i.test(ua)) {
        deviceType = 'tablet';
      }
      
      // Формируем имя устройства
      const deviceName = `${browser} на ${deviceType === 'mobile' ? 'мобильном' : deviceType === 'tablet' ? 'планшете' : 'компьютере'}`;
      
      return {
        browser,
        deviceType,
        deviceName
      };
    },
    // === Управление баннерами ===
    hideBannerForever(bannerType) {
      localStorage.setItem(`hide_${bannerType}_banner`, 'forever');
      this[`hide${bannerType.charAt(0).toUpperCase() + bannerType.slice(1)}Banner`] = true;
    },
    hideBannerForDays(bannerType, days) {
      const hideUntil = new Date();
      hideUntil.setDate(hideUntil.getDate() + days);
      localStorage.setItem(`hide_${bannerType}_banner`, hideUntil.toISOString());
      this[`hide${bannerType.charAt(0).toUpperCase() + bannerType.slice(1)}Banner`] = true;
    },
    checkBannerVisibility(bannerType) {
      const hideStatus = localStorage.getItem(`hide_${bannerType}_banner`);
      if (!hideStatus) {
        return false; // Не скрыт
      }
      if (hideStatus === 'forever') {
        return true; // Скрыт навсегда
      }
      // Проверяем, не истекло ли время скрытия
      const hideUntil = new Date(hideStatus);
      if (hideUntil > new Date()) {
        return true; // Еще скрыт
      }
      // Время истекло, удаляем флаг
      localStorage.removeItem(`hide_${bannerType}_banner`);
      return false;
    },
    initializeBannerVisibility() {
      this.hidePwaInstallBanner = this.checkBannerVisibility('pwaInstall');
      this.hidePushBanner = this.checkBannerVisibility('push');
      this.hideTelegramBanner = this.checkBannerVisibility('telegram');
      this.hideMaxBanner = this.checkBannerVisibility('max');
      this.hideSmtpDisabledBanner = this.checkSmtpBannerVisibility();
    },
    checkSmtpBannerVisibility() {
      if (!this.company?.id) return false;
      const key = `smtp_disabled_notice_hidden_${this.company.id}`;
      const raw = localStorage.getItem(key);
      if (!raw) return false;
      try {
        const data = JSON.parse(raw);
        if (data.until && new Date(data.until) > new Date()) return true;
      } catch (_) {}
      localStorage.removeItem(key);
      return false;
    },
    hideSmtpBannerForDays(days) {
      if (!this.company?.id) return;
      const until = new Date();
      until.setDate(until.getDate() + days);
      localStorage.setItem(`smtp_disabled_notice_hidden_${this.company.id}`, JSON.stringify({ until: until.toISOString() }));
      this.hideSmtpDisabledBanner = true;
    },
    subscribeToPushMessages() {
      const unsubscribe = subscribeToMessages((payload) => {
        console.log('📨 Получено foreground push-уведомление:', payload);
        
        const notificationType = payload.data?.type;
        const data = payload.data || {};
        const isUnifiedFeedPush = Boolean(data.user_notification_id);
        const skipForegroundToast = this.shouldSkipForegroundPushToast(notificationType);

        if (isUnifiedFeedPush) {
          this.refreshNotificationsFeed();
        }

        if (!skipForegroundToast) {
          if (isUnifiedFeedPush) {
            this.showLegacyToastForUnifiedPush(payload, data, notificationType);
          } else {
            this.handleLegacyPushNotification(payload, data, notificationType);
          }
          this.showForegroundBrowserNotification(payload);
        }
      });

      if (unsubscribe) {
        this.$once('hook:beforeDestroy', unsubscribe);
      }
    },
    handleLegacyPushNotification(payload, data, notificationType) {
      switch (notificationType) {
          case 'task':
            // Индивидуальная задача
            this.$notifyTask(
              data.task_name || 'Задача',
              data.task_time || '',
              data.task_id
            );
            break;
            
          case 'daily_tasks':
            // Задачи на день
            this.$notifyDailyTasks(
              data.count || 0,
              data.date || 'сегодня'
            );
            break;
            
          case 'task_reminder':
            // Напоминание о задаче
            this.$notifyTaskReminder(
              data.task_name || 'Задача',
              data.task_time || '',
              data.time_until || '',
              data.task_id
            );
            break;
            
          case 'regular_payment':
            // Регулярный платеж
            this.$notifyRegularPayment(
              data.recipient || 'Платеж',
              data.amount || null,
              data.payment_id
            );
            break;
            
          case 'subscription_updated':
            // Обновление подписки
            this.$notifySubscriptionUpdated(
              data.plan_name || 'Подписка',
              data.user_limit || 'без ограничений',
              data.modules || null,
              data.expires_at || ''
            );
            break;

          case 'subscription_expiring':
            // Подписка истекает (< 3 дней)
            this.$notifySubscriptionExpiring(
              data.days_remaining || 2,
              data.expires_at || '',
              data.subscription_id
            );
            break;
            
          case 'company_invitation':
            // Приглашение в компанию
            this.$notifyCompanyInvitation(
              data.company_name || 'Компания',
              data.company_id
            );
            break;
            
          case 'new_order_request':
            // Новая заявка
            this.$notifyNewOrderRequest(
              data.customer_name || 'Клиент',
              data.ticket_key || '',
              data.order_request_id
            );
            break;
            
          case 'new_email_in_request':
            // Новое письмо в заявке
            this.$notifyNewEmailInRequest(
              data.ticket_key || '',
              data.from || null,
              data.order_request_id
            );
            break;
            
          case 'missed_call':
            // Входящий звонок обрабатывается через WebSocket (GotNewNovofonNotification)
            // для foreground режима, поэтому здесь пропускаем чтобы избежать дублирования
            // missed_call push пропущен (обрабатывается через WebSocket)
            break;
            
          case 'new_order':
            // Новый заказ с безналом
            this.$notifyNewOrder(
              data.number || '',
              data.customer_name || null,
              data.order_id
            );
            break;
            
          case 'order_in_print_queue':
            // Заказ в очередь на печать
            this.$notifyOrderInPrintQueue(
              data.number || '',
              data.order_id
            );
            break;
          
          case 'comment_mention':
            // Упоминание в комментарии
            this.$notifyCommentMention({
              authorName: data.author_name || 'Пользователь',
              orderNumber: data.order_number || data.order_id || '',
              customerName: data.customer_name || 'Клиент',
              commentText: data.comment_text || 'Комментарий',
              orderId: data.order_id
            });
            break;
          
          // Воронка продаж: уведомления
          case 'overdue_task':
            // Просроченная задача по лиду
            this.$notifyOverdueTask(
              payload.notification?.title || 'Просроченная задача',
              payload.notification?.body || 'Задача просрочена',
              data.notification_id,
              data.leadable_type,
              data.leadable_id
            );
            break;
            
          case 'unassigned_lead':
            // Лид без ответственного
            this.$notifyUnassignedLead(
              payload.notification?.title || 'Лид без ответственного',
              payload.notification?.body || 'Лид не назначен',
              data.notification_id,
              data.leadable_type,
              data.leadable_id
            );
            break;
            
          case 'high_priority_no_activity':
            // Высокоприоритетный лид без активности
            this.$notifyHighPriorityNoActivity(
              payload.notification?.title || 'Высокоприоритетный лид без активности',
              payload.notification?.body || 'Лид без активности',
              data.notification_id,
              data.leadable_type,
              data.leadable_id
            );
            break;
            
          case 'conversion_milestone':
            // Достижение целевого показателя конверсии
            this.$notifyConversionMilestone(
              payload.notification?.title || 'Достигнут целевой показатель!',
              payload.notification?.body || 'Поздравляем!',
              data.notification_id
            );
            break;
            
          case 'no_call_reminder':
            // Напоминание - клиент не звонил более 14 дней
            this.$notifyNoCallReminder(
              payload.notification?.title || 'Напоминание: связаться с клиентом',
              payload.notification?.body || 'Клиент не звонил',
              data.notification_id,
              data.leadable_type,
              data.leadable_id
            );
            break;
            
          case 'customer_task_created':
            // Новая задача по клиенту
            this.$notifyCustomerTaskCreated(
              payload.notification?.title || 'Новая задача по клиенту',
              payload.notification?.body || 'Создана новая задача',
              data.notification_id,
              data.leadable_type,
              data.leadable_id,
              data.task_id
            );
            break;
            
          case 'stage_progression':
            // Переход на новый этап воронки
            this.$notifyStageProgression(
              payload.notification?.title || 'Переход на новый этап',
              payload.notification?.body || 'Лид перешел на новый этап',
              data.notification_id,
              data.leadable_type,
              data.leadable_id
            );
            break;
            
          case 'lead_assigned':
            // Назначение/снятие менеджера с лида
            this.$notifyLeadAssigned(
              payload.notification?.title || 'Назначение на лида',
              payload.notification?.body || 'Изменение назначения',
              data.notification_id,
              data.leadable_type,
              data.leadable_id
            );
            break;
            
          case 'high_lead_score':
            // Высокий приоритет лида (score >= 80)
            this.$notifyHighLeadScore(
              payload.notification?.title || 'Высокий приоритет лида!',
              payload.notification?.body || 'Лид достиг высокого приоритета',
              data.notification_id,
              data.leadable_type,
              data.leadable_id
            );
            break;
            
          case 'incoming_call':
            // Входящий звонок (через Novofon) - обрабатывается через WebSocket
            // Но можем показать Toast если WebSocket не сработал
            // incoming_call push получен (может дублироваться с WebSocket)
            // Пропускаем, чтобы избежать дублирования с GotNewNovofonNotification
            break;
          
          default:
            console.warn('⚠️ Неизвестный тип push-уведомления:', notificationType);
            // Показываем общее уведомление
            this.$notify({
              title: payload.notification?.title || 'CRM МояФабрика',
              message: payload.notification?.body || 'Новое уведомление',
              icon: 'mdi-bell',
              type: 'info'
            });
        }
    },
    showLegacyToastForUnifiedPush(payload, data, notificationType) {
      const title = payload.notification?.title || '';
      const body = payload.notification?.body || '';
      const entityId = data.entity_id ? Number(data.entity_id) : null;

      switch (notificationType) {
        case 'task_reminder':
          this.$notifyTaskReminder(
            title.replace(/^Напоминание:\s*/, '') || 'Задача',
            body || '',
            '',
            entityId
          );
          break;
        case 'call_missed':
          this.$notifyCallMissed(
            title.replace(/^Пропущенный звонок:?\s*/, '') || 'Неизвестный',
            body || '',
            data.route || null
          );
          break;
        default:
          break;
      }
    },
    showForegroundBrowserNotification(payload) {
        const title = payload.notification?.title || 'CRM МояФабрика';
        const body = payload.notification?.body || 'Новое уведомление';
        const icon = payload.notification?.icon || '/img/icons/web-app-manifest-192x192.png';
        
        let uniqueTag = resolvePushNotificationTag(payload.data || {});
        
        if ('serviceWorker' in navigator && 'Notification' in window && Notification.permission === 'granted') {
          navigator.serviceWorker.ready.then((registration) => {
            registration.getNotifications({ tag: uniqueTag }).then((existingNotifications) => {
              if (existingNotifications.length === 0) {
                registration.showNotification(title, {
                  body: body,
                  icon: icon,
                  badge: '/img/icons/favicon-96x96.png',
                  data: payload.data,
                  tag: uniqueTag,
                  requireInteraction: false,
                  silent: false,
                  vibrate: [200, 100, 200],
                });
              }
            }).catch(() => {
              registration.showNotification(title, {
                body: body,
                icon: icon,
                badge: '/img/icons/favicon-96x96.png',
                data: payload.data,
                tag: uniqueTag,
                requireInteraction: false,
                silent: false,
                vibrate: [200, 100, 200],
              });
            });
          }).catch(() => {
            if ('Notification' in window && Notification.permission === 'granted') {
              const notification = new Notification(title, {
                body: body,
                icon: icon,
                badge: '/img/icons/favicon-96x96.png',
                data: payload.data,
                tag: uniqueTag,
                requireInteraction: false,
                silent: false,
              });
              
              notification.onclick = (event) => {
                event.preventDefault();
                window.focus();
                notification.close();
              };
            }
          });
        }
    },
    async requestPushPermission() {
      if (!this.pushSupported) {
        this.$swal.fire({
          icon: 'warning',
          title: 'Push-уведомления не поддерживаются',
          text: 'Ваш браузер не поддерживает push-уведомления'
        });
        return;
      }
      
      try {
        // КРИТИЧЕСКИ ВАЖНО: Сначала запрашиваем разрешение!
        const { requestNotificationPermission } = await import('@/firebase/pushNotifications');
        const permission = await requestNotificationPermission();
        
        // Обновляем локальное состояние разрешения
        this.pushPermission = permission;
        
        if (permission === 'granted') {
          // Только после получения разрешения получаем токен
          await this.registerPushToken();
          
          this.$swal.fire({
            icon: 'success',
            title: 'Отлично!',
            text: 'Push-уведомления включены',
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000
          });
        } else if (permission === 'denied') {
          this.$swal.fire({
            icon: 'warning',
            title: 'Разрешение отклонено',
            text: 'Разрешение на push-уведомления было отклонено. Вы можете включить его в настройках браузера.',
            confirmButtonText: 'Понятно'
          });
        } else {
          // permission === 'default' - пользователь закрыл диалог без выбора
          console.log('Разрешение не предоставлено (default)');
        }
      } catch (error) {
        errorHandler.handle(error, { showDialog: false, logToConsole: false });
      this.$swal.fire({
          icon: 'error',
          title: 'Ошибка',
          text: 'Не удалось запросить разрешение на push-уведомления. Попробуйте позже.',
          confirmButtonText: 'Понятно'
        });
      }
    },
    refreshNotificationsFeed() {
      this.$root.$emit('notifications:refresh')
    },
    async markUserNotificationReadFromPush(notificationId) {
      if (!notificationId) {
        return
      }
      try {
        await axios.post(`user-notifications/${notificationId}/read`)
        this.$root.$emit('notifications:updated')
      } catch (error) {
        errorHandler.handle(error, { showDialog: false })
      }
    },
    navigateFromPushNotification(targetUrl, data = {}) {
      if (data.user_notification_id) {
        this.markUserNotificationReadFromPush(data.user_notification_id)
      }
      if (targetUrl) {
        this.$router.push(targetUrl).catch(() => {})
      }
    },
    shouldSkipForegroundPushToast(notificationType) {
      if (!notificationType) {
        return false
      }
      // Toast уже показан через WebSocket-обработчики в listenWS()
      if ([
        'call_incoming',
        'incoming_call',
        'tochka_payment',
        'order_request',
        'order_request_email',
      ].includes(notificationType)) {
        return true
      }
      if (notificationType.startsWith('funnel_')) {
        return true
      }
      return false
    },
    listenWS() {
      if (!this.user || !this.user.id || !this.company || !this.company.id) {
        return;
      }
      
      this.channel = Echo.private(`users.${this.user.id}.company.${this.company.id}`);
      
      // Подписываемся на канал для конкретного пользователя для событий завершения сессий
      this.userChannel = Echo.channel(`user.${this.user.id}`);
      
      // Подписываемся на канал для новостей Telegram
      this.telegramChannel = Echo.channel('telegram-news');
      
      
      this.channel.listen('GotNewOrderRequest', (e) => {
        const request = e.orderRequest || {};
        const customerName = [request.surname, request.name].filter(Boolean).join(' ').trim() || 'Клиент';
        this.$notifyNewOrderRequest(
          customerName,
          request.ticket_key || `#${request.id}`,
          request.id
        );
        this.$store.dispatch('increaseNewOrderRequestsCount')
        this.refreshNotificationsFeed()
      });
      this.channel.listen('NewEmailReceived', (e) => {
        // Не трогаем new_order_requests_count: бейдж «Заявки» = число заявок со статусом «new»,
        // входящее письмо по существующей заявке не увеличивает этот счётчик (иначе бейдж «1» при нуле новых заявок).
        this.$swal.fire({
          icon: 'info',
          title: 'Новое сообщение',
          text: `Получено новое сообщение в заявке #${e.orderRequestId}`,
          timer: 5000,
          showConfirmButton: false,
          timerProgressBar: true
        });
        this.refreshNotificationsFeed()
      });
      
      // Обработчик изменения статуса заявки (слушаем на канале пользователя)
      this.channel.listen('OrderRequestStatusChanged', (e) => {
        console.log('🔔 OrderRequestStatusChanged received:', e);
        // Если статус изменился с 'new' на любой другой, уменьшаем счетчик
        if (e.oldStatus === 'new' && e.newStatus !== 'new') {
          console.log('✅ Уменьшаем счетчик бейджей (new -> ' + e.newStatus + ')');
          this.$store.dispatch('reduceNewOrderRequestsCount')
        }
        // Если статус изменился на 'new' с любого другого, увеличиваем счетчик
        else if (e.oldStatus !== 'new' && e.newStatus === 'new') {
          console.log('✅ Увеличиваем счетчик бейджей (' + e.oldStatus + ' -> new)');
          this.$store.dispatch('increaseNewOrderRequestsCount')
        } else {
          console.log('ℹ️ Статус изменился, но счетчик не меняется:', e.oldStatus, '->', e.newStatus);
        }
      });

      // Обработчик для новых постов в Telegram канале
      this.telegramChannel.listen('NewTelegramPost', (e) => {
        this.$notifyTelegramNews(e.post.text, e.post.is_pinned);
      });


      // Обработчик нового платежа Точка Банк (вебхук)
      this.channel.listen('GotNewTochkaPayment', (e) => {
        const amount = (e.amount != null) ? Number(e.amount).toLocaleString('ru-RU') : '0';
        this.$notifyTochkaPayment(amount, e.is_income, e.payer || '', e.payment_id || null, e.short_purpose || '');
        this.refreshNotificationsFeed()
      });

      // Обработчик для входящего звонка (Novofon) через WebSocket
      this.channel.listen('GotNewNovofonNotification', (e) => {
        
        // Используем toast-уведомление вместо SWAL
        this.$notifyMissedCall(
          e.call_details["user_name"] || 'Неизвестный',
          e.call_details["phone"] || '',
          e.call_details["is_my_customer"] ? e.call_details["customer_id"] : null,
          null, // duration - не передается в этом событии
          e.call_details["is_spam"] === true
        );
        this.refreshNotificationsFeed()
      });

      // Обработчик для уведомлений воронки продаж
      this.channel.listen('FunnelNotificationCreated', (e) => {
        console.log('📊 FunnelNotificationCreated received:', e);
        
        const notification = e.notification;
        if (!notification) {
          return;
        }

        // Вызываем соответствующий метод в зависимости от типа уведомления
        switch (notification.type) {
          case 'overdue_task':
            this.$notifyOverdueTask(
              notification.title,
              notification.message,
              notification.id,
              notification.leadable_type,
              notification.leadable_id
            );
            break;
            
          case 'unassigned_lead':
            this.$notifyUnassignedLead(
              notification.title,
              notification.message,
              notification.id,
              notification.leadable_type,
              notification.leadable_id
            );
            break;
            
          case 'high_priority_no_activity':
            this.$notifyHighPriorityNoActivity(
              notification.title,
              notification.message,
              notification.id,
              notification.leadable_type,
              notification.leadable_id
            );
            break;
            
          case 'conversion_milestone':
            this.$notifyConversionMilestone(
              notification.title,
              notification.message,
              notification.id
            );
            break;
            
          case 'no_call_reminder':
            this.$notifyNoCallReminder(
              notification.title,
              notification.message,
              notification.id,
              notification.leadable_type,
              notification.leadable_id
            );
            break;
            
          case 'customer_task_created':
            this.$notifyCustomerTaskCreated(
              notification.title,
              notification.message,
              notification.id,
              notification.leadable_type,
              notification.leadable_id,
              notification.metadata?.task_id
            );
            break;
            
          case 'stage_progression':
            this.$notifyStageProgression(
              notification.title,
              notification.message,
              notification.id,
              notification.leadable_type,
              notification.leadable_id
            );
            break;
            
          case 'lead_assigned':
            this.$notifyLeadAssigned(
              notification.title,
              notification.message,
              notification.id,
              notification.leadable_type,
              notification.leadable_id
            );
            break;
            
          case 'high_lead_score':
            this.$notifyHighLeadScore(
              notification.title,
              notification.message,
              notification.id,
              notification.leadable_type,
              notification.leadable_id
            );
            break;
            
          default:
            // Общее уведомление для неизвестных типов
            this.$notify({
              title: notification.title,
              message: notification.message,
              icon: 'mdi-bell',
              type: 'info',
              onClick: () => {
                this.$router.push('/funnel');
              }
            });
        }

        this.refreshNotificationsFeed()
      });

      // Обработчик для завершения сессий пользователя
      this.userChannel.listen('sessions.revoked', (e) => {
        this.$swal.fire({
          icon: 'warning',
          title: 'Сессии завершены',
          text: e.message || 'Ваши сессии были завершены администратором',
          confirmButtonText: 'Войти заново',
          allowOutsideClick: false,
          allowEscapeKey: false,
          showCancelButton: false
        }).then(() => {
          // Очищаем localStorage и токены
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('company');
          
          // Перенаправляем на страницу авторизации
          this.$store.dispatch("LOGOUT");
          this.$router.push('/login');
        });
      });
    },
    handleResize() {
      // Закрываем drawer при изменении размера на мобильный
      if (this.$vuetify.breakpoint.mobile || this.forcedMobileMode) {
        this.drawer = false;
      }
    },
    onDrawerChange(value) {
      // ОТКЛЮЧЕНО согласно правилам: Vuetify сам управляет overlay и z-index
      // НЕ нужно программно изменять display, opacity, z-index для overlay
      return;
    },
    updateCSSVariables() {
      // Устанавливаем CSS-переменные из настроек
      const primaryColor = this.$store.getters.company?.settings?.primary_color || '#1976d2';
      document.documentElement.style.setProperty('--primary-color', primaryColor);
      document.documentElement.style.setProperty('--button-save-color', primaryColor);
      document.documentElement.style.setProperty('--button-confirm-color', primaryColor);
    },
    removeAllOverlays() {
      // ОТКЛЮЧЕНО согласно правилам: Vuetify сам управляет overlay и z-index
      // НЕ нужно программно изменять display, opacity, z-index для overlay
      // Vuetify автоматически показывает/скрывает overlay при открытии/закрытии dialog
      return;
    },
    async exitCustomerMode() {
      try {
        if (this.$route.path !== '/') {
          await this.$router.replace('/').catch(() => {});
        }
        await this.$store.dispatch('stopCustomerImpersonation');
      } catch (error) {
        this.$swal.fire({
          icon: 'error',
          title: 'Не удалось выйти из режима клиента',
          text: error?.body?._humanMessage || error?.body?.message || 'Повторите попытку',
        });
      }
    }
  },
  updated() {
    this.$vuetify.theme.themes.light.primary = this.$store.getters.company?.settings?.primary_color ? this.$store.getters.company?.settings?.primary_color : this.$vuetify.theme.themes.light.primary
    // Обновляем CSS-переменные при каждом обновлении
    this.updateCSSVariables();
  },
  mounted() {
    // ФИКС: Устанавливаем РЕАЛЬНУЮ высоту viewport для мобильных
    this.setRealViewportHeight();
    
    // Для iOS - вызываем с задержкой, чтобы получить правильную высоту после скрытия адресной строки
    setTimeout(() => {
      this.setRealViewportHeight();
    }, 100);
    
    // Слушатели на изменение размера
    window.addEventListener('resize', this.setRealViewportHeight);
    window.addEventListener('orientationchange', () => {
      setTimeout(this.setRealViewportHeight, 100);
    });
    
    // Слушатель на visualViewport (более точный для мобильных)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.setRealViewportHeight);
      // УБРАЛИ scroll listener - он вызывал смещение нижнего меню
      // window.visualViewport.addEventListener('scroll', this.setRealViewportHeight);
    }
    
    // Нижнее меню: pin после появления shell (v-if) и при возврате в PWA
    this.ensureBottomNavigationPinned();
    window.addEventListener('crm:pin-bottom-nav', this.ensureBottomNavigationPinned);
    
    // КРИТИЧНЫЙ ФИКС: принудительно убираем overflow через JavaScript
    // Это нужно потому что Vuetify применяет стили ПОСЛЕ наших CSS
    if (window.innerWidth <= 959) {
      this.fixMobileOverflow();
      
      // Повторяем через 100ms для гарантии (после полной загрузки Vuetify)
      setTimeout(() => {
        this.fixMobileOverflow();
      }, 100);
      
      // И через 500ms на случай медленной загрузки
      setTimeout(() => {
        this.fixMobileOverflow();
      }, 500);
    }
    
    // Обработчик для переключения drawer
    this.$on('toggle-drawer', () => {
      this.isMobileMode = !this.isMobileMode;
      this.drawer = !this.drawer;
    });
    
    // Обработчик изменения размера окна
    window.addEventListener('resize', this.handleResize);
    
    // Push: Обработчик навигации при клике на уведомление
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        // Явно НЕ возвращаем true, чтобы избежать ошибки "message channel closed"
        // Это односторонняя коммуникация от Service Worker к странице
        if (event.data && event.data.type === 'NOTIFICATION_CLICK') {
          this.navigateFromPushNotification(event.data.url, event.data.data || {});
        }
        // НЕ возвращаем true - это предотвращает ожидание ответа через MessageChannel
      });
    }
    
    // iOS PWA: Обработка навигации из уведомления при запуске приложения
    // Проверяем URL параметр from_notification=true
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('from_notification') === 'true') {
      // На iOS PWA навигация происходит через прямой URL
      // Просто удаляем параметр, текущий путь уже правильный
      const cleanUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, document.title, cleanUrl);
    }
    
    // iOS PWA: Проверяем, есть ли отложенная навигация в localStorage
    // (на случай если iOS не поддерживает client.navigate)
    try {
      const pendingNav = localStorage.getItem('pendingNavigation');
      if (pendingNav && pendingNav !== this.$route.path) {
        localStorage.removeItem('pendingNavigation');
        this.navigateFromPushNotification(pendingNav, {});
      }
    } catch (e) {
      // Игнорируем ошибки localStorage
    }
    
    // PWA: Перехватываем промпт установки
    window.addEventListener('beforeinstallprompt', (e) => {
      // Предотвращаем автоматический показ промпта
      e.preventDefault();
      // Сохраняем промпт для дальнейшего использования
      this.deferredPrompt = e;
      // Показываем кнопку установки
      this.showInstallButton = true;
    });
    
    // PWA: Обработчик успешной установки
    window.addEventListener('appinstalled', (e) => {
      this.showInstallButton = false;
      this.deferredPrompt = null;
      
      // Уведомление об успешной установке
      this.$swal.fire({
        icon: 'success',
        title: 'Отлично!',
        text: 'CRM МояФабрика установлена на ваше устройство',
        timer: 3000,
        showConfirmButton: false,
        position: 'top-end',
        backdrop: false
      });
    });
    
    // PWA: Проверяем, уже установлено ли приложение
    this.checkPWAInstalled();
    
    // Push-уведомления: Инициализируем после логина
    if (this.showMenu && !this.isImpersonating) {
      // ВАЖНО: Инициализируем device_id ПЕРВЫМ делом
      this.deviceId = this.getOrCreateDeviceId();
      
      this.initializePushNotifications();
      
      // Запрашиваем актуальный статус push с сервера
      this.$store.dispatch('getAuthUser').then(response => {
        if (response && typeof response.push_connected_on_device !== 'undefined') {
          this.pushConnectedOnDevice = response.push_connected_on_device;
          
          // Принудительно обновляем Vue для пересчета computed properties
          this.$nextTick(() => {
            this.$forceUpdate();
          });
        }
      }).catch(() => {
        // Игнорируем ошибку
      });
    }
    
    // Инициализируем видимость баннеров
    this.initializeBannerVisibility();
    
    
    // Устанавливаем CSS-переменные при загрузке
    this.updateCSSVariables();
    
    // Удаляем overlay при загрузке в мобильном режиме
    this.removeAllOverlays();
  },
  beforeDestroy() {
    // Очищаем обработчики скролла для нижнего меню
    if (this._bottomNavScrollHandlers) {
      this._bottomNavScrollHandlers.forEach(cleanup => cleanup());
      this._bottomNavScrollHandlers = null;
    }
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('crm:pin-bottom-nav', this.ensureBottomNavigationPinned);
    
    // Очищаем WebSocket каналы
    if (this.channel && this.user && this.user.id && this.company && this.company.id) {
      Echo.leaveChannel(`users.${this.user.id}.company.${this.company.id}`);
    }
    if (this.userChannel && this.user && this.user.id) {
      Echo.leaveChannel(`user.${this.user.id}`);
    }
  },
  watch: {
    "company.id": function (newCompanyId) {
      if (newCompanyId && this.user && this.user.id) {
        this.listenWS();
      }
      this.hideSmtpDisabledBanner = this.checkSmtpBannerVisibility();
    },
    // Закрываем drawer при переходе по меню в мобильном режиме
    $route() {
      if (this.isMobile || this.forcedMobileMode) {
        this.drawer = false;
        // Принудительно удаляем overlay при навигации
        this.removeAllOverlays();
        
        // КРИТИЧНЫЙ ФИКС: применяем overflow фикс при каждой навигации
        this.$nextTick(() => {
          this.fixMobileOverflow();
        });
      }
    },
    drawer(newValue) {
      // Просто отслеживаем состояние drawer, не вмешиваемся в его работу
      // isMobileMode управляется только через кнопку меню
    },
    forcedMobileMode(newValue) {
      // Если включаем принудительный режим, закрываем drawer
      if (newValue) {
        this.drawer = false;
        this.ensureBottomNavigationPinned();
      } else {
        // Если выключаем, возвращаем обычное состояние
        this.drawer = !this.$vuetify.breakpoint.mobile;
      }
    },
    isMobile(newValue) {
      if (newValue) {
        // Если экран становится мобильным, закрываем drawer
        this.drawer = false;
        this.ensureBottomNavigationPinned();
      } else if (!this.forcedMobileMode) {
        // Если экран становится десктопным, открываем основное меню
        this.drawer = true;
      }
    },
    // Отслеживаем загрузку пользователя для инициализации push
    showMenu(newValue) {
      if (newValue) {
        // Меню монтируется через v-if — pin после появления в DOM
        this.ensureBottomNavigationPinned();
      }
      if (newValue && !this.isImpersonating && !this.deviceId) {
        // Инициализируем device_id
        this.deviceId = this.getOrCreateDeviceId();
        
        // Инициализируем push
        this.initializePushNotifications();
        
        // Запрашиваем актуальный статус push с сервера
        this.$store.dispatch('getAuthUser').then(response => {
          if (response && typeof response.push_connected_on_device !== 'undefined') {
            this.pushConnectedOnDevice = response.push_connected_on_device;
            
            // Принудительно обновляем Vue
            this.$nextTick(() => {
              this.$forceUpdate();
            });
          }
        }).catch(() => {
          // Игнорируем ошибку
        });
      }
    }
  },
  computed: {
    ...mapGetters(['jwt', 'routes', 'user', 'visibleRoutes', 'company', 'branch', 'availableBranchSelect', 'onCustomHost', 'forcedMobileMode', 'impersonation', 'isImpersonating', 'isCustomer', 'maxBotConfigured']),
    isMobile() {
      return this.$vuetify.breakpoint.mobile;
    },
    showMenu() {
      return this.jwt && this.user && this.user.company_context_id && this.$route.name !== "form";
    },
    appVersion() {
      return APP_VERSION;
    },
    // PWA: Показываем кнопку установки только если не в standalone режиме и не скрыт баннер
    canInstallPWA() {
      return this.showInstallButton && !this.checkPWAInstalled() && !this.hidePwaInstallBanner;
    },
    // Push: Проверяем, подключено ли ТЕКУЩЕЕ устройство (по данным из backend)
    isCurrentDeviceConnected() {
      // Используем флаг из backend - это надежно!
      return this.pushConnectedOnDevice;
    },
    // Push: Показываем кнопку push только если ТЕКУЩЕЕ устройство не подключено и не скрыт баннер
    canEnablePushOnThisDevice() {
      return this.pushSupported && !this.isCurrentDeviceConnected && !this.hidePushBanner;
    },
    // Telegram: Показываем баннер только если не подключен и не скрыт
    shouldShowTelegramBanner() {
      return !this.user?.tg_channel_id && !this.hideTelegramBanner;
    },
    // MAX: баннер только если бот настроен на сервере, пользователь ещё не привязан и баннер не скрыт
    shouldShowMaxBanner() {
      return this.maxBotConfigured && !this.user?.max_user_id && !this.hideMaxBanner;
    },
    // SMTP: Показываем баннер когда CRM SMTP отключен админом и у компании нет своего SMTP
    shouldShowSmtpDisabledBanner() {
      if (!this.company?.id || !this.showMenu) return false;
      const shouldShow = !this.company.has_own_smtp && this.company.crm_smtp_disabled;
      return shouldShow && !this.hideSmtpDisabledBanner;
    },
    subscriptionExpiresSoon() {
      if (!this.company?.subscription?.expires_at) {
        return false;
      }
      const expirationDate = new Date(this.company.subscription.expires_at);
      const now = new Date();
      const diffTime = expirationDate - now;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      // Показываем уведомление за 3 дня до истечения
      return diffDays <= 3 && diffDays > 0;
    },
    daysUntilExpiration() {
      if (!this.company?.subscription?.expires_at) {
        return 0;
      }
      const expirationDate = new Date(this.company.subscription.expires_at);
      const now = new Date();
      const diffTime = expirationDate - now;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      return Math.max(0, diffDays);
    },
  },
  
});
</script>
<style lang="scss">
/* Глобальные централизованные стили */
@import '@/styles/notifications-common.scss';
@import '@/styles/navigation-common.scss';
@import '@/styles/modals-common.scss';

/* Баннеры с кнопками управления */
.banner-alert {
  .v-alert__content {
    width: 100%;
  }
  
  .d-flex.gap-2 {
    gap: 8px;
    display: flex;
    align-items: center;
  }
  
  // Мобильная адаптация
  @media (max-width: 768px) {
    .shrink {
      flex-direction: column;
      align-items: stretch;
      width: 100%;
      margin-top: 8px;
      
      .v-btn {
        width: 100%;
      }
    }
  }
}

/* Унификация стилей меню - все пункты имеют одинаковую структуру */
.v-navigation-drawer .v-list.sidebar-menu .v-list-item {
  padding: 0 16px !important;
  min-height: 40px !important;
  margin-bottom: 2px !important;
}

.v-navigation-drawer .v-list.sidebar-menu .v-list-item .v-list-item__icon {
  margin-right: 12px !important;
}

.v-navigation-drawer .v-list.sidebar-menu .v-list-item .v-list-item__content {
  padding: 0 !important;
  margin: 0 !important;
}

/* Убираем дополнительные отступы у v-list-group */
.v-navigation-drawer .v-list.sidebar-menu .v-list-group {
  padding: 0 !important;
  margin: 0 !important;
}

.v-navigation-drawer .v-list.sidebar-menu .v-list-group .v-list-group__header {
  padding: 0 !important;
  margin: 0 !important;
}

/* Стрелочка раскрытия подменю ближе к тексту */
.v-navigation-drawer .v-list.sidebar-menu .v-list-group .v-list-group__header__append-icon {
  margin-left: 8px !important;
}

/* Подпункты меню - иконка выровнена под текстом родителя */
.v-navigation-drawer .v-list.sidebar-menu .v-list-group__items {
  padding-left: 0 !important;
}

.v-navigation-drawer .v-list.sidebar-menu .v-list-group__items .v-list-item {
  padding-left: 52px !important;
}

/* Новогодний пункт меню "Итоги года" */
.v-navigation-drawer .v-list.sidebar-menu .new-year-menu-item {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  border-radius: 8px !important;
  margin: 8px 4px !important;
  padding: 8px 12px !important;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4) !important;
  position: relative;
  overflow: hidden;
}

/* Принудительно делаем иконку белой */
.v-navigation-drawer .v-list.sidebar-menu .new-year-menu-item .v-list-item__icon {
  color: white !important;
}

.v-navigation-drawer .v-list.sidebar-menu .new-year-menu-item .v-list-item__icon .v-icon {
  color: white !important;
}

.v-navigation-drawer .v-list.sidebar-menu .new-year-menu-item .v-list-item__icon .v-icon::before {
  color: white !important;
}

.v-navigation-drawer .v-list.sidebar-menu .new-year-menu-item::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: linear-gradient(
    45deg,
    transparent 30%,
    rgba(255, 255, 255, 0.1) 50%,
    transparent 70%
  );
  animation: shine 3s infinite;
}

@keyframes shine {
  0% {
    transform: translateX(-100%) translateY(-100%) rotate(45deg);
  }
  100% {
    transform: translateX(100%) translateY(100%) rotate(45deg);
  }
}

.v-navigation-drawer .v-list.sidebar-menu .new-year-menu-item:hover {
  background: linear-gradient(135deg, #764ba2 0%, #667eea 100%) !important;
  box-shadow: 0 6px 16px rgba(102, 126, 234, 0.6) !important;
  transform: translateY(-2px);
  transition: all 0.3s ease;
}

.v-navigation-drawer .v-list.sidebar-menu .new-year-menu-item .v-list-item__icon,
.v-navigation-drawer .v-list.sidebar-menu .new-year-menu-item .v-list-item__icon .v-icon,
.v-navigation-drawer .v-list.sidebar-menu .new-year-menu-item .v-list-item__icon i,
.v-navigation-drawer .v-list.sidebar-menu .new-year-menu-item .v-list-item__icon::before,
.v-navigation-drawer .v-list.sidebar-menu .new-year-menu-item .new-year-icon,
.v-navigation-drawer .v-list.sidebar-menu .new-year-menu-item .new-year-icon i,
.v-navigation-drawer .v-list.sidebar-menu .new-year-menu-item .new-year-icon::before,
.v-navigation-drawer .v-list.sidebar-menu .new-year-menu-item:hover .v-list-item__icon,
.v-navigation-drawer .v-list.sidebar-menu .new-year-menu-item:hover .v-list-item__icon .v-icon,
.v-navigation-drawer .v-list.sidebar-menu .new-year-menu-item.v-list-item--active .v-list-item__icon,
.v-navigation-drawer .v-list.sidebar-menu .new-year-menu-item.v-list-item--active .v-list-item__icon .v-icon {
  color: white !important;
  fill: white !important;
}

.v-navigation-drawer .v-list.sidebar-menu .new-year-menu-item .new-year-icon {
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

.v-navigation-drawer .v-list.sidebar-menu .new-year-menu-item .new-year-title {
  color: white !important;
  font-weight: 600 !important;
  font-size: 15px !important;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.income {
  background: #dfffae;
}

.expense {
  background: #ffaed0;
}

.expecting {
  background: #ebebeb;
}





.swal2-styled.swal2-confirm:focus {
  box-shadow: none !important;
}

/* Максимально специфичный селектор для переопределения SWAL */
body .swal2-popup .swal2-styled.swal2-confirm {
  background-color: var(--swal-primary-color, #770303) !important;
  background: var(--swal-primary-color, #770303) !important;
  border-color: var(--swal-primary-color, #770303) !important;
}

.woot-widget-bubble {
  bottom: 90px !important;
}

@media (min-width: 678px) {
  .woot-widget-bubble {
    bottom: 20px !important;
  }
}

/* Scroll-lock для AppDialog: без position:fixed на body */
html.app-dialog-scroll-lock,
html.app-dialog-scroll-lock body {
  overflow: hidden !important;
  overscroll-behavior: none !important;
}

/* iOS PWA: меньше rubber-band рассинхрона fixed bottom-nav */
html,
body {
  overscroll-behavior-y: none;
}

/* Нижнее меню: всегда к низу viewport; safe-area только в padding/height */
.v-bottom-navigation.bottom-nav {
  position: fixed !important;
  bottom: 0 !important; /* НЕ поднимать через safe-area-inset — иначе «отрыв» */
  left: 0 !important;
  right: 0 !important;
  top: auto !important;
  width: 100% !important;
  max-width: 100% !important;
  z-index: 100 !important;
  margin: 0 !important;
  margin-bottom: 0 !important;
  /* home-indicator / notch: увеличиваем высоту, не смещаем bottom */
  padding-bottom: env(safe-area-inset-bottom, 0px) !important;
  min-height: 56px !important;
  height: calc(56px + env(safe-area-inset-bottom, 0px)) !important;
  max-height: calc(56px + env(safe-area-inset-bottom, 0px)) !important;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1) !important;
  /* без will-change/translate — меньше шансов на iOS containing-block баги */
  transform: none !important;
  -webkit-transform: none !important;
  transition: none !important;
}

@supports (-webkit-touch-callout: none) {
  .v-bottom-navigation.bottom-nav {
    position: fixed !important;
    bottom: 0 !important;
    left: 0 !important;
    right: 0 !important;
    top: auto !important;
    width: 100% !important;
    max-width: 100% !important;
    margin: 0 !important;
    touch-action: manipulation !important;
  }
}

/* Удалены все глобальные оверрайды overlay/z-index — управление на стороне Vuetify */

/* Исправление проблемы с размытием при изменении размера экрана */
@media (max-width: 959px) {
  /* Корень приложения не должен иметь transform — иначе fixed у bottom-nav
     привязывается к приложению, а не к экрану (отрыв меню в PWA). */
  .v-application {
    transform: none !important;
    -webkit-transform: none !important;
  }

  /* Vuetify wrap: backface-visibility:hidden создаёт containing block для fixed
     в WebKit — меню цепляется к wrap (растёт с контентом) вместо экрана. */
  .v-application--wrap {
    -webkit-backface-visibility: visible !important;
    backface-visibility: visible !important;
    transform: none !important;
    -webkit-transform: none !important;
    min-height: 100vh !important;
    min-height: 100dvh !important;
  }

  /* .v-main МОЖЕТ иметь transform — pull-to-refresh смещает только контент */
  
  /* ФИКС СКРОЛЛА SIDEBAR МЕНЮ */
  .v-navigation-drawer {
    height: 100vh !important;
    height: 100dvh !important;
    overflow-y: auto !important;
  }
  
  .v-navigation-drawer .v-navigation-drawer__content {
    height: auto !important;
    min-height: 100% !important;
    overflow-y: visible !important;
  }
  
  .v-navigation-drawer .v-list.sidebar-menu {
    padding-bottom: 300px !important;
    margin-bottom: 0 !important;
  }
  
  /* Убраны все вмешательства в z-index/overlay в мобильном режиме */
  
  /* Убираем размытие с main контента в мобильном режиме */
  body:has(.v-bottom-navigation) .v-main {
    filter: none !important;
  }
  
  /* Обеспечиваем правильный padding для контента */
  .v-main.mobile-main {
    /* ИСПРАВЛЕНО: bottom-navigation высотой 56px, а не 64px! */
    padding-bottom: calc(56px + env(safe-area-inset-bottom, 0) + 8px) !important;
  }
  
  /* Убираем стандартный padding Vuetify для мобильного режима */
  .v-main.mobile-main .v-main__wrap {
    padding-bottom: 0 !important;
  }
}

/* Дополнительные фиксы для плавной работы скролла на iOS */
@supports (-webkit-overflow-scrolling: touch) {
  body {
    /* Улучшаем скролл на iOS */
    -webkit-overflow-scrolling: touch !important;
    overflow-scrolling: touch !important;
  }
  
  .v-main {
    /* УБРАЛИ overflow-y: auto - он создавал второй скролл! */
    /* Оставляем только overflow-x для предотвращения горизонтального скролла */
    overflow-x: hidden !important;
    -webkit-overflow-scrolling: touch !important;
  }
  
}

/* Новогодняя кнопка в нижнем меню */
.v-bottom-navigation.bottom-nav .new-year-bottom-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  border-radius: 12px !important;
  margin: 4px !important;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.5) !important;
  position: relative;
  overflow: hidden;
}

.v-bottom-navigation.bottom-nav .new-year-bottom-btn::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: linear-gradient(
    45deg,
    transparent 30%,
    rgba(255, 255, 255, 0.2) 50%,
    transparent 70%
  );
  animation: shine 3s infinite;
}

.v-bottom-navigation.bottom-nav .new-year-bottom-btn .v-icon {
  color: white !important;
  animation: pulse 2s ease-in-out infinite;
}

.v-bottom-navigation.bottom-nav .new-year-bottom-btn span {
  color: white !important;
  font-weight: 600 !important;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.v-bottom-navigation.bottom-nav .new-year-bottom-btn.v-btn--active {
  background: linear-gradient(135deg, #764ba2 0%, #667eea 100%) !important;
  box-shadow: 0 6px 16px rgba(102, 126, 234, 0.7) !important;
}
</style>
<style lang="scss">
/* Финальный оверрайд после всех стилей: убираем тень у диалогов */
body .v-dialog,
#app .v-dialog,
.v-application .v-dialog {
  box-shadow: none !important;
  -webkit-box-shadow: none !important;
}

/* Дополнительная защита от горизонтального скролла внутри модалок */
.v-dialog .editor-modal i.v-icon,
.v-dialog .editor-modal .v-input__icon i.v-icon {
  display: inline-block !important;
  overflow: hidden !important;
  max-width: 100% !important;
}
</style>